import React, { useState, useEffect, useCallback } from 'react';
import { Card, Tabs, Row, Col, Select, DatePicker, Button, Space, Divider, Typography, Table, App } from 'antd';
import { ClearOutlined, DownloadOutlined } from '@ant-design/icons';
import ActivityIndicator from '../../../components/ActivityIndicator';
import { useDiaryTeacher } from '../../../hooks/useDiaryTeacher';
import { diaryTeacherAdminApi } from '../../../services/diary-teacher/diary-teacher.service';
import type { ColumnsType } from 'antd/es/table';
import { DiaryRecordItem, AttendanceRecordItem } from '../../../services/diary-teacher/diary-teacher.types';
import dayjs from 'dayjs';
import DiaryRichTextCell from './DiaryRichTextCell';
import { richTextToPlainText } from './DiaryTeacher.utils';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Componente personalizado para filtro de rango de fechas
const DateRangeFilter: React.FC<{
  onFilter: (dates: [string, string] | null) => void;
  value?: [string, string] | null;
}> = ({ onFilter, value }) => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    value ? [dayjs(value[0], 'DD/MM/YYYY'), dayjs(value[1], 'DD/MM/YYYY')] : null
  );

  const handleDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const startDate = dates[0].format('DD/MM/YYYY');
      const endDate = dates[1].format('DD/MM/YYYY');
      setDateRange(dates);
      onFilter([startDate, endDate]);
    } else {
      setDateRange(null);
      onFilter(null);
    }
  };

  return (
    <div style={{ padding: '8px' }}>
      <RangePicker
        style={{ width: '100%' }}
        format="DD/MM/YYYY"
        value={dateRange}
        onChange={handleDateChange}
        placeholder={['Fecha inicio', 'Fecha fin']}
      />
    </div>
  );
};

const DiaryTeacher: React.FC = () => {
  const { message } = App.useApp();
  const {
    diaryRecords,
    attendanceRecords,
    schools,
    grades,
    weeks,
    diaryLoading,
    attendanceLoading,
    schoolsLoading,
    gradesLoading,
    weeksLoading,
    diaryPagination,
    attendancePagination,
    filters,
    loadDiaryRecords,
    loadAttendanceRecords,
    loadGrades,
    updateFilters,
    clearFilters,
  } = useDiaryTeacher();

  const [activeTab, setActiveTab] = useState('diary');

  // Estados para filtros de columna
  const [columnFilters, setColumnFilters] = useState<{
    diary: { [key: string]: any };
    attendance: { [key: string]: any };
  }>({
    diary: {},
    attendance: {},
  });

  // Estado especial para filtros de rango de fechas
  const [dateRangeFilters, setDateRangeFilters] = useState<{
    diary: [string, string] | null;
    attendance: [string, string] | null;
  }>({
    diary: null,
    attendance: null,
  });

  // Estados para valores de filtros
  const [allFilterValues, setAllFilterValues] = useState<{
    schools: { text: string; value: string }[];
    grades: { text: string; value: string }[];
    weeks: { text: string; value: string }[];
    teachers: { text: string; value: string }[];
    dates: { text: string; value: string }[];
    classrooms: { text: string; value: string }[];
    subjects: { text: string; value: string }[];
    courses: { text: string; value: string }[];
    lessons: { text: string; value: string }[];
    students: { text: string; value: string }[];
    contentQualities: { text: string; value: string }[];
  }>({
    schools: [],
    grades: [],
    weeks: [],
    teachers: [],
    dates: [],
    classrooms: [],
    subjects: [],
    courses: [],
    lessons: [],
    students: [],
    contentQualities: [],
  });

  // Cargar datos cuando cambian los filtros o la tab activa
  useEffect(() => {
    if (activeTab === 'diary') {
      loadDiaryRecords();
    } else {
      loadAttendanceRecords();
    }
  }, [filters, activeTab, loadDiaryRecords, loadAttendanceRecords]);

  // Cargar grados cuando cambia la escuela
  useEffect(() => {
    if (filters.schoolId) {
      loadGrades(filters.schoolId);
    } else {
      loadGrades();
    }
  }, [filters.schoolId, loadGrades]);

  // Cargar todos los valores para filtros
  const loadAllFilterValues = useCallback(async () => {
    try {
      // Cargar escuelas
      const schoolsData = await diaryTeacherAdminApi.getSchools();
      const schoolsOptions = schoolsData.map(school => ({
        text: school.name,
        value: school.name,
      }));

      // Cargar grados
      const gradesData = await diaryTeacherAdminApi.getGrades();
      const gradesOptions = gradesData.map(grade => ({
        text: grade.name,
        value: grade.name,
      }));

      // Cargar semanas
      const weeksData = await diaryTeacherAdminApi.getWeeks();
      const weeksOptions = weeksData.map(week => ({
        text: week.name,
        value: week.id.toString(),
      }));

      // Para otros valores (profesores, fechas, etc.), necesito obtenerlos de los datos
      // Cargar datos para extraer valores únicos
      const [diaryResponse, attendanceResponse] = await Promise.all([
        diaryTeacherAdminApi.getDiaryRecords({ page: 1, limit: 10000 }),
        diaryTeacherAdminApi.getAttendanceRecords({ page: 1, limit: 10000 }),
      ]);

      const allDiaryData = diaryResponse.data || [];
      const allAttendanceData = attendanceResponse.data || [];

      // Extraer valores únicos
      const teachersSet = new Set<string>();
      const datesSet = new Set<string>();
      const classroomsSet = new Set<string>();
      const subjectsSet = new Set<string>();
      const coursesSet = new Set<string>();
      const lessonsSet = new Set<string>();
      const studentsSet = new Set<string>();
      const contentQualitiesSet = new Set<string>();

      // Procesar datos del diario
      allDiaryData.forEach(record => {
        teachersSet.add(`${record.teacher_first_name} ${record.teacher_last_name}`);
        datesSet.add(dayjs(record.date).format('DD/MM/YYYY'));
        classroomsSet.add(record.classroom_custom_code);
        if (record.content_quality) contentQualitiesSet.add(record.content_quality);
        record.subjects?.forEach(subject => subjectsSet.add(subject));
        record.courses?.forEach(course => coursesSet.add(course));
        record.lessons?.forEach(lesson => lessonsSet.add(lesson));
      });

      // Procesar datos de asistencia
      allAttendanceData.forEach(record => {
        teachersSet.add(`${record.teacher_first_name} ${record.teacher_last_name}`);
        datesSet.add(dayjs(record.date).format('DD/MM/YYYY'));
        classroomsSet.add(record.classroom_custom_code);
        studentsSet.add(record.student_name);
      });

      setAllFilterValues({
        schools: schoolsOptions,
        grades: gradesOptions,
        weeks: weeksOptions,
        teachers: Array.from(teachersSet).map(teacher => ({ text: teacher, value: teacher })),
        dates: Array.from(datesSet).map(date => ({ text: date, value: date })),
        classrooms: Array.from(classroomsSet).map(classroom => ({ text: classroom, value: classroom })),
        subjects: Array.from(subjectsSet).map(subject => ({ text: subject, value: subject })),
        courses: Array.from(coursesSet).map(course => ({ text: course, value: course })),
        lessons: Array.from(lessonsSet).map(lesson => ({ text: lesson, value: lesson })),
        students: Array.from(studentsSet).map(student => ({ text: student, value: student })),
        contentQualities: Array.from(contentQualitiesSet).map(quality => ({ text: quality, value: quality })),
      });
    } catch (error) {
      console.error('Error loading filter values:', error);
    }
  }, []);

  // Cargar valores de filtros al iniciar
  useEffect(() => {
    loadAllFilterValues();
  }, [loadAllFilterValues]);

  // Manejar cambios en filtros de columna
  const handleTableChange = useCallback((pagination: any, filters: any, sorter: any, extra: any) => {
    const tableType = activeTab;
    
    setColumnFilters(prev => ({
      ...prev,
      [tableType]: filters,
    }));

    // Convertir filtros de columna a formato del backend
    const backendFilters: any = {
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
    };

    // Mapear filtros específicos
    if (filters.school_name?.length) {
      // Encontrar el ID de la escuela por nombre
      const school = schools.find(s => s.name === filters.school_name[0]);
      if (school) backendFilters.schoolId = school.id;
    }

    if (filters.grade_name?.length) {
      // Encontrar el ID del grado por nombre
      const grade = grades.find(g => g.name === filters.grade_name[0]);
      if (grade) backendFilters.gradeId = grade.id;
    }

    if (filters.week?.length) {
      backendFilters.week = parseInt(filters.week[0]);
    }

    // Agregar filtro de rango de fechas si existe
    const currentDateRange = dateRangeFilters[tableType as keyof typeof dateRangeFilters];
    if (currentDateRange) {
      backendFilters.startDate = dayjs(currentDateRange[0], 'DD/MM/YYYY').format('YYYY-MM-DD');
      backendFilters.endDate = dayjs(currentDateRange[1], 'DD/MM/YYYY').format('YYYY-MM-DD');
    }

    updateFilters(backendFilters);
  }, [activeTab, schools, grades, dateRangeFilters, updateFilters]);

  // Manejar cambio en filtro de rango de fechas
  const handleDateRangeFilter = useCallback((dates: [string, string] | null) => {
    const tableType = activeTab;
    
    setDateRangeFilters(prev => ({
      ...prev,
      [tableType]: dates,
    }));

    // Aplicar filtros actuales más el nuevo filtro de fecha
    const currentFilters = columnFilters[tableType as keyof typeof columnFilters];
    const backendFilters: any = {
      page: 1, // Reset a primera página cuando se aplica filtro
      limit: 10,
    };

    // Mantener filtros existentes
    if (currentFilters.school_name?.length) {
      const school = schools.find(s => s.name === currentFilters.school_name[0]);
      if (school) backendFilters.schoolId = school.id;
    }

    if (currentFilters.grade_name?.length) {
      const grade = grades.find(g => g.name === currentFilters.grade_name[0]);
      if (grade) backendFilters.gradeId = grade.id;
    }

    if (currentFilters.week?.length) {
      backendFilters.week = parseInt(currentFilters.week[0]);
    }

    // Agregar filtro de fecha
    if (dates) {
      backendFilters.startDate = dayjs(dates[0], 'DD/MM/YYYY').format('YYYY-MM-DD');
      backendFilters.endDate = dayjs(dates[1], 'DD/MM/YYYY').format('YYYY-MM-DD');
    }

    updateFilters(backendFilters);
  }, [activeTab, columnFilters, schools, grades, updateFilters]);

  // Función para exportar datos a CSV
  const exportToCSV = (data: any[], filename: string, headers: { [key: string]: string }) => {
    if (data.length === 0) {
      message.warning('No hay datos para exportar');
      return;
    }

    // Crear encabezados CSV
    const csvHeaders = Object.values(headers).join(',');
    
    // Crear filas CSV
    const csvRows = data.map(row => {
      return Object.keys(headers).map(key => {
        let value = row[key];
        
        // Manejar diferentes tipos de datos
        if (Array.isArray(value)) {
          value = value.join('; '); // Unir arrays con punto y coma
        } else if (typeof value === 'object' && value !== null) {
          // Para objetos complejos como incidents
          if (key === 'incidents') {
            value = value.map((incident: any) => `${incident.student_names}: ${incident.description}`).join('; ');
          } else if (key === 'photos') {
            value = `${value.length} foto(s)`;
          } else {
            value = JSON.stringify(value);
          }
        } else if (typeof value === 'boolean') {
          value = value ? 'Sí' : 'No';
        } else if (value === null || value === undefined) {
          value = '';
        }
        
        // Escapar comillas y envolver en comillas si contiene comas
        value = String(value).replace(/"/g, '""');
        if (value.includes(',') || value.includes('\n') || value.includes('"')) {
          value = `"${value}"`;
        }
        
        return value;
      }).join(',');
    });

    // Combinar encabezados y filas
    const csvContent = [csvHeaders, ...csvRows].join('\n');
    
    // Crear y descargar el archivo
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // \ufeff es BOM para UTF-8
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success(`Archivo ${filename} exportado exitosamente`);
  };

  // Función para exportar registros del diario
  const exportDiaryRecords = async () => {
    const headers = {
      school_name: 'Escuela',
      date: 'Fecha',
      grade_name: 'Grado',
      teacher_name: 'Profesor',
      classroom_custom_code: 'Clase',
      week: 'Semana',
      subjects: 'Asignaturas',
      courses: 'Cursos',
      lessons: 'Lecciones',
      activities: 'Actividades',
      content_quality: 'Calidad de Contenido',
      student_participation: 'Participación',
      class_performance: 'Rendimiento',
      performance_comment: 'Comentario Rendimiento',
      details: 'Detalles',
      incidents: 'Incidencias',
      photos: 'Fotos'
    };

    try {
      // Construir filtros actuales basados en los filtros de columna y rangos de fechas
      const currentFilters = columnFilters.diary;
      const currentDateRange = dateRangeFilters.diary;
      
      const exportFilters: any = {
        page: 1,
        limit: 1000000, // Obtener todos los registros para export
      };

      // Agregar filtros de columna
      if (currentFilters.school_name?.length) {
        const school = schools.find(s => s.name === currentFilters.school_name[0]);
        if (school) exportFilters.schoolId = school.id;
      }

      if (currentFilters.grade_name?.length) {
        const grade = grades.find(g => g.name === currentFilters.grade_name[0]);
        if (grade) exportFilters.gradeId = grade.id;
      }

      if (currentFilters.week?.length) {
        exportFilters.week = parseInt(currentFilters.week[0]);
      }

      // Agregar filtro de rango de fechas
      if (currentDateRange) {
        exportFilters.startDate = dayjs(currentDateRange[0], 'DD/MM/YYYY').format('YYYY-MM-DD');
        exportFilters.endDate = dayjs(currentDateRange[1], 'DD/MM/YYYY').format('YYYY-MM-DD');
      }

      console.log('Filtros de exportación para diario:', exportFilters);

      const response = await diaryTeacherAdminApi.getDiaryRecords(exportFilters);

      const allRecords = response.data || [];

      if (allRecords.length === 0) {
        message.warning('No hay registros del diario para exportar con los filtros aplicados');
        return;
      }

      // Transformar datos para incluir nombre completo del profesor
      const transformedData = allRecords.map(record => ({
        ...record,
        teacher_name: `${record.teacher_first_name} ${record.teacher_last_name}`,
        date: dayjs(record.date).format('DD/MM/YYYY'),
        courses: record.courses?.join('; ') || '',
        lessons: record.lessons?.join('; ') || '',
        activities: record.activities?.join('; ') || '',
        subjects: record.subjects?.join('; ') || '',
        performance_comment: richTextToPlainText(record.performance_comment),
        details: richTextToPlainText(record.details),
        incidents: (record.incidents || []).map(incident => ({
          ...incident,
          description: richTextToPlainText(incident.description),
        })),
      }));

      exportToCSV(transformedData, 'reporte_diario_maestro', headers);
    } catch (error) {
      console.error('Error exporting diary records:', error);
      message.error('Error al exportar registros del diario');
    }
  };

  // Función para exportar registros de asistencia
  const exportAttendanceRecords = async () => {
    const headers = {
      school_name: 'Escuela',
      date: 'Fecha',
      grade_name: 'Grado',
      teacher_name: 'Profesor',
      classroom_custom_code: 'Clase',
      week: 'Semana',
      student_name: 'Estudiante',
      present: 'Presente',
      justified: 'Justificado',
      justification_reason: 'Razón de Justificación'
    };

    try {
      // Construir filtros actuales basados en los filtros de columna y rangos de fechas
      const currentFilters = columnFilters.attendance;
      const currentDateRange = dateRangeFilters.attendance;
      
      const exportFilters: any = {
        page: 1,
        limit: 1000000, // Obtener todos los registros para export
      };

      // Agregar filtros de columna
      if (currentFilters.school_name?.length) {
        const school = schools.find(s => s.name === currentFilters.school_name[0]);
        if (school) exportFilters.schoolId = school.id;
      }

      if (currentFilters.grade_name?.length) {
        const grade = grades.find(g => g.name === currentFilters.grade_name[0]);
        if (grade) exportFilters.gradeId = grade.id;
      }

      if (currentFilters.week?.length) {
        exportFilters.week = parseInt(currentFilters.week[0]);
      }

      // Agregar filtro de rango de fechas
      if (currentDateRange) {
        exportFilters.startDate = dayjs(currentDateRange[0], 'DD/MM/YYYY').format('YYYY-MM-DD');
        exportFilters.endDate = dayjs(currentDateRange[1], 'DD/MM/YYYY').format('YYYY-MM-DD');
      }

      console.log('Filtros de exportación para asistencias:', exportFilters);

      const response = await diaryTeacherAdminApi.getAttendanceRecords(exportFilters);

      const allRecords = response.data || [];

      if (allRecords.length === 0) {
        message.warning('No hay registros de asistencia para exportar con los filtros aplicados');
        return;
      }

      const transformedData = allRecords.map(record => ({
        ...record,
        teacher_name: `${record.teacher_first_name} ${record.teacher_last_name}`,
        date: dayjs(record.date).format('DD/MM/YYYY'),
        present: record.present ? 'Sí' : 'No',
        justified: record.present ? '-' : (record.justified ? 'Sí' : 'No'),
        justification_reason: record.present ? '-' : (record.justification_reason || '-')
      }));

      exportToCSV(transformedData, 'reporte_asistencia', headers);
    } catch (error) {
      console.error('Error exporting attendance records:', error);
      message.error('Error al exportar registros de asistencia');
    }
  };

  // Columnas para la tabla del diario
  const diaryColumns: ColumnsType<DiaryRecordItem> = [
    {
      title: 'Escuela',
      dataIndex: 'school_name',
      key: 'school_name',
      width: 160,
      filters: allFilterValues.schools,
      filteredValue: columnFilters.diary.school_name || null,
      onFilter: (value, record) => record.school_name === value,
      filterSearch: true,
    },
    {
      title: 'Fecha',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <DateRangeFilter
          onFilter={(dates) => {
            handleDateRangeFilter(dates);
            confirm();
          }}
          value={dateRangeFilters.diary}
        />
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? 'var(--bo-primary)' : undefined }}>📅</span>
      ),
      filteredValue: dateRangeFilters.diary ? ['range'] : null,
    },
    {
      title: 'Grado',
      dataIndex: 'grade_name',
      key: 'grade_name',
      width: 100,
      filters: allFilterValues.grades,
      filteredValue: columnFilters.diary.grade_name || null,
      onFilter: (value, record) => record.grade_name === value,
      filterSearch: true,
    },
    {
      title: 'Profesor',
      key: 'teacher',
      width: 190,
      render: (record: DiaryRecordItem) => (
        `${record.teacher_first_name} ${record.teacher_last_name}`
      ),
      filters: allFilterValues.teachers,
      filteredValue: columnFilters.diary.teacher || null,
      onFilter: (value, record) => `${record.teacher_first_name} ${record.teacher_last_name}` === value,
      filterSearch: true,
    },
    {
      title: 'Clase',
      dataIndex: 'classroom_custom_code',
      key: 'classroom_custom_code',
      width: 120,
      filters: allFilterValues.classrooms,
      filteredValue: columnFilters.diary.classroom_custom_code || null,
      onFilter: (value, record) => record.classroom_custom_code === value,
      filterSearch: true,
    },
    {
      title: 'Semana',
      dataIndex: 'week',
      key: 'week',
      width: 100,
      filters: allFilterValues.weeks,
      filteredValue: columnFilters.diary.week || null,
      onFilter: (value, record) => record.week.toString() === value,
      filterSearch: true,
    },
    {
      title: 'Asignaturas',
      dataIndex: 'subjects',
      key: 'subjects',
      width: 120,
      render: (subjects: string[]) => subjects.join(', '),
      filters: allFilterValues.subjects,
      filteredValue: columnFilters.diary.subjects || null,
      onFilter: (value, record) => record.subjects.includes(value as string),
      filterSearch: true,
    },
    {
      title: 'Cursos',
      dataIndex: 'courses',
      key: 'courses',
      width: 190,
      render: (courses: string[]) => courses.length > 0 ? courses.join(', ') : '-',
      filters: allFilterValues.courses,
      filteredValue: columnFilters.diary.courses || null,
      onFilter: (value, record) => record.courses.includes(value as string),
      filterSearch: true,
    },
    {
      title: 'Lecciones',
      dataIndex: 'lessons',
      key: 'lessons',
      width: 150,
      render: (lessons: string[]) => lessons.length > 0 ? lessons.join(', ') : '-',
      filters: allFilterValues.lessons,
      filteredValue: columnFilters.diary.lessons || null,
      onFilter: (value, record) => record.lessons.includes(value as string),
      filterSearch: true,
    },
    {
      title: 'Actividades',
      dataIndex: 'activities',
      key: 'activities',
      width: 200,
      render: (activities: string[], record: DiaryRecordItem) => {
        if (!activities || activities.length === 0) return '-';
        return (
          <ul style={{ margin: 0, paddingLeft: '16px', listStyleType: 'disc' }}>
            {activities.map((activity, index) => (
              <li key={`${record.id}-activity-${index}-${activity.substring(0, 10)}`} style={{ marginBottom: '2px' }}>
                {activity}
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      title: 'Calidad de Contenido',
      dataIndex: 'content_quality',
      key: 'content_quality',
      width: 150,
      render: (content_quality: string) => content_quality || '-',
      filters: allFilterValues.contentQualities,
      filteredValue: columnFilters.diary.content_quality || null,
      onFilter: (value, record) => record.content_quality === value,
      filterSearch: true,
    },
    {
      title: 'Participación',
      dataIndex: 'student_participation',
      key: 'student_participation',
      width: 120,
    },
    {
      title: 'Rendimiento de la Clase',
      dataIndex: 'class_performance',
      key: 'class_performance',
      width: 120,
    },
    {
      title: 'Comentario Rendimiento',
      dataIndex: 'performance_comment',
      key: 'performance_comment',
      width: 800,
      render: (comment?: string | null) => <DiaryRichTextCell value={comment} />,
    },
    {
      title: 'Detalles',
      dataIndex: 'details',
      key: 'details',
      width: 600,
      render: (details?: string | null) => <DiaryRichTextCell value={details} />,
    },
    {
      title: 'Incidencias',
      dataIndex: 'incidents',
      key: 'incidents',
      width: 600,
      render: (incidents: DiaryRecordItem['incidents']) => (
        incidents.length > 0 ? (
          <div>
            {incidents.map(incident => (
              <div key={incident.id} style={{ fontSize: '12px', marginBottom: '4px' }}>
                <strong>{incident.student_names}:</strong>{' '}
                <DiaryRichTextCell value={incident.description} />
              </div>
            ))}
          </div>
        ) : '-'
      ),
    },
    {
      title: 'Fotos',
      dataIndex: 'photos',
      key: 'photos',
      width: 80,
      render: (photos: DiaryRecordItem['photos']) => (
        photos.length > 0 ? (
          <span>{photos.length} foto(s)</span>
        ) : '-'
      ),
    },
  ];

  // Columnas para la tabla de asistencias
  const attendanceColumns: ColumnsType<AttendanceRecordItem> = [
    {
      title: 'Escuela',
      dataIndex: 'school_name',
      key: 'school_name',
      width: 180,
      filters: allFilterValues.schools,
      filteredValue: columnFilters.attendance.school_name || null,
      onFilter: (value, record) => record.school_name === value,
      filterSearch: true,
    },
    {
      title: 'Fecha',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <DateRangeFilter
          onFilter={(dates) => {
            handleDateRangeFilter(dates);
            confirm();
          }}
          value={dateRangeFilters.attendance}
        />
      ),
      filterIcon: (filtered: boolean) => (
        <span style={{ color: filtered ? 'var(--bo-primary)' : undefined }}>📅</span>
      ),
      filteredValue: dateRangeFilters.attendance ? ['range'] : null,
    },
    {
      title: 'Grado',
      dataIndex: 'grade_name',
      key: 'grade_name',
      width: 120,
      filters: allFilterValues.grades,
      filteredValue: columnFilters.attendance.grade_name || null,
      onFilter: (value, record) => record.grade_name === value,
      filterSearch: true,
    },
    {
      title: 'Profesor',
      key: 'teacher',
      width: 200,
      render: (record: AttendanceRecordItem) => (
        `${record.teacher_first_name} ${record.teacher_last_name}`
      ),
      filters: allFilterValues.teachers,
      filteredValue: columnFilters.attendance.teacher || null,
      onFilter: (value, record) => `${record.teacher_first_name} ${record.teacher_last_name}` === value,
      filterSearch: true,
    },
    {
      title: 'Clase',
      dataIndex: 'classroom_custom_code',
      key: 'classroom_custom_code',
      width: 200,
      filters: allFilterValues.classrooms,
      filteredValue: columnFilters.attendance.classroom_custom_code || null,
      onFilter: (value, record) => record.classroom_custom_code === value,
      filterSearch: true,
    },
    /* {
      title: 'Semana',
      dataIndex: 'week',
      key: 'week',
      width: 100,
      filters: allFilterValues.weeks,
      filteredValue: columnFilters.attendance.week || null,
      onFilter: (value, record) => record.week.toString() === value,
      filterSearch: true,
    }, */
    {
      title: 'Estudiante',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 200,
      filters: allFilterValues.students,
      filteredValue: columnFilters.attendance.student_name || null,
      onFilter: (value, record) => record.student_name === value,
      filterSearch: true,
    },
    {
      title: 'Presente',
      dataIndex: 'present',
      key: 'present',
      width: 120,
      render: (present: boolean) => (
        <span style={{ color: present ? 'green' : 'red' }}>
          {present ? 'Sí' : 'No'}
        </span>
      ),
      filters: [
        { text: 'Presente', value: 'true' },
        { text: 'Ausente', value: 'false' },
      ],
      onFilter: (value, record) => record.present.toString() === value,
    },
    {
      title: 'Justificado',
      dataIndex: 'justified',
      key: 'justified',
      width: 140,
      render: (justified: boolean, record: AttendanceRecordItem) => {
        // Si el estudiante está presente, no mostrar información de justificación
        if (record.present) {
          return <span style={{ color: 'gray' }}></span>;
        }
        
        return (
          <span style={{ color: justified ? 'orange' : 'gray' }}>
            {justified ? 'Sí' : 'No'}
          </span>
        );
      },
      filters: [
        { text: 'Justificado', value: 'true' },
        { text: 'No Justificado', value: 'false' },
      ],
      onFilter: (value, record) => {
        // Solo aplicar filtro si el estudiante está ausente
        if (record.present) return false;
        return record.justified.toString() === value;
      },
    },
    {
      title: 'Razón',
      dataIndex: 'justification_reason',
      key: 'justification_reason',
      width: 250,
      ellipsis: true,
      render: (reason: string | null, record: AttendanceRecordItem) => {
        // Si el estudiante está presente, no mostrar razón de justificación
        if (record.present) {
          return <span style={{ color: 'gray' }}></span>;
        }
        
        return reason || '-';
      },
    },
  ];

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  if (schoolsLoading) {
    return <ActivityIndicator />;
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Diario del Maestro
          </Title>
          <Text type="secondary">
            Visualiza y administra todos los registros del diario y asistencias de los profesores
          </Text>
        </Col>
        <Col>
          <Space>
            <Button 
              icon={<ClearOutlined />} 
              onClick={() => {
                setColumnFilters({ diary: {}, attendance: {} });
                setDateRangeFilters({ diary: null, attendance: null });
                updateFilters({ page: 1, limit: 10, sortBy: 'date', sortDirection: 'desc' });
              }}
            >
              Limpiar filtros
            </Button>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={exportDiaryRecords}
            >
              Exportar Diario
            </Button>
            <Button 
              type="primary" 
              ghost
              icon={<DownloadOutlined />}
              onClick={exportAttendanceRecords}
            >
              Exportar Asistencias
            </Button>
          </Space>
        </Col>
      </Row>

      <Divider />

      {/* Tabs y Tablas */}
      <Card>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Registros del Diario" key="diary">
            {/* Tabla de Ant Design con configuración básica */}
            <Table
              columns={diaryColumns}
              dataSource={diaryRecords}
              loading={diaryLoading}
              rowKey={(record) => record.id}
              pagination={{
                current: diaryPagination.current,
                pageSize: diaryPagination.pageSize,
                total: diaryPagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total: number, range: [number, number]) => 
                  `${range[0]}-${range[1]} de ${total} registros`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 3200 }}
              size="small"
            />
          </TabPane>

          <TabPane tab="Registros de Asistencia" key="attendance">
            <Table
              columns={attendanceColumns}
              dataSource={attendanceRecords}
              loading={attendanceLoading}
              rowKey={(record) => record.id}
              pagination={{
                current: attendancePagination.current,
                pageSize: attendancePagination.pageSize,
                total: attendancePagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total: number, range: [number, number]) => 
                  `${range[0]}-${range[1]} de ${total} registros`,
              }}
              onChange={handleTableChange}
              scroll={{ x: 1630 }}
              size="small"
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default DiaryTeacher;
